/*
  foodora eShop – cart behaviour (drawer + cart page)
  Depends only on window.FD (fd-core.js). Markup comes from snippets/cart-line.liquid,
  sections/cart-drawer.liquid and sections/main-cart.liquid.

  - Quantity / remove changes are debounced, sent through FD.cart.change() and the
    cart sections re-render themselves (data-fd-render regions).
  - The drawer is never opened from here on cart events; callers use FD.openCart().
  - Without JS the forms post `updates[]` + checkout to /cart.
*/
(function () {
  'use strict';

  var FD = window.FD;
  if (!FD || !FD.cart) return;

  var DEBOUNCE_MS = 400;
  var DRAWER_SELECTOR = '[data-fd-cart-drawer]';
  var OPENER_SELECTOR = '[data-fd-open="#fd-cart-drawer"]';

  var timers = {};
  var queue = Promise.resolve();

  // Lets CSS hide the no-JS "Update" button.
  document.documentElement.classList.add('fd-cart-js');

  /* ---------- Lookup helpers ---------- */
  function sectionRoot(id) {
    var roots = document.querySelectorAll('[data-fd-cart-section]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].getAttribute('data-fd-cart-section') === id) return roots[i];
    }
    return null;
  }

  function linesOf(root) {
    return root ? Array.prototype.slice.call(root.querySelectorAll('[data-fd-cart-line]')) : [];
  }

  function findLine(root, key) {
    var lines = linesOf(root);
    for (var i = 0; i < lines.length; i++) {
      if (lines[i].getAttribute('data-key') === key) return lines[i];
    }
    return null;
  }

  function focusRole(li, role) {
    if (!li || !role) return false;
    var controls = li.querySelectorAll('[data-fd-focus="' + role + '"]');
    for (var i = 0; i < controls.length; i++) {
      if (!controls[i].disabled && controls[i].getAttribute('aria-disabled') !== 'true') {
        controls[i].focus();
        return true;
      }
    }
    return false;
  }

  /* ---------- Pending state (affected line only) ---------- */
  function setPending(li, pending) {
    li.classList.toggle('is-pending', pending);
    if (pending) li.setAttribute('aria-busy', 'true');
    else li.removeAttribute('aria-busy');
    Array.prototype.forEach.call(li.querySelectorAll('button, input'), function (el) {
      el.disabled = pending;
    });
    var remove = li.querySelector('[data-fd-cart-remove]');
    if (remove) {
      if (pending) remove.setAttribute('aria-disabled', 'true');
      else remove.removeAttribute('aria-disabled');
    }
  }

  function showError(li, message) {
    var out = li.querySelector('[data-fd-line-error]');
    if (out) out.textContent = message || '';
  }

  /* ---------- Change pipeline ---------- */
  function perform(rootId, key) {
    var root = sectionRoot(rootId);
    var li = root && findLine(root, key);
    if (!li) return Promise.resolve();

    var input = li.querySelector('.fd-qty__input');
    if (!input) return Promise.resolve();
    var qtyEl = li.querySelector('fd-qty');
    var previous = parseInt(input.defaultValue, 10);
    if (isNaN(previous)) previous = 0;
    var quantity = parseInt(input.value, 10);
    if (isNaN(quantity) || quantity < 0) quantity = previous;
    if (quantity === previous) return Promise.resolve();

    var active = document.activeElement;
    var role = active && li.contains(active) ? active.getAttribute('data-fd-focus') : null;
    var position = linesOf(root).indexOf(li);

    showError(li, '');
    setPending(li, true);

    return FD.cart
      .change(parseInt(li.getAttribute('data-line'), 10), quantity, 'cart')
      .then(function () {
        FD.announce(quantity === 0 ? FD.t('cart_removed') || FD.t('cart_updated') : FD.t('cart_updated'));
        var freshRoot = sectionRoot(rootId);
        var fresh = freshRoot && findLine(freshRoot, key);
        if (fresh) {
          focusRole(fresh, role || 'input');
          return;
        }
        // Line is gone: move focus to the line that took its place.
        var rest = linesOf(freshRoot);
        if (rest.length) {
          focusRole(rest[Math.min(position, rest.length - 1)], 'input');
        } else {
          var cta = freshRoot && freshRoot.querySelector('.fd-cart-empty a');
          if (cta) cta.focus();
        }
      })
      .catch(function (err) {
        // Cart may have changed even if rendering failed: resync quietly.
        if (!err || !err.status) FD.cart.refresh().then(upgradeOpeners, function () {});
        if (!li.isConnected) return;
        setPending(li, false);
        input.value = String(previous);
        if (qtyEl) qtyEl.value = previous;
        showError(li, (err && err.message) || FD.t('error_generic'));
        focusRole(li, role || 'input');
      });
  }

  function schedule(li, immediate) {
    var root = li.closest('[data-fd-cart-section]');
    if (!root) return;
    var rootId = root.getAttribute('data-fd-cart-section');
    var key = li.getAttribute('data-key');
    clearTimeout(timers[key]);
    var run = function () {
      delete timers[key];
      queue = queue.then(function () { return perform(rootId, key); });
    };
    timers[key] = setTimeout(run, immediate ? 0 : DEBOUNCE_MS);
  }

  function flush(li) {
    var key = li.getAttribute('data-key');
    if (timers[key] == null) return;
    schedule(li, true);
  }

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input.matches || !input.matches('.fd-qty__input')) return;
    var li = input.closest('[data-fd-cart-line]');
    if (!li || li.classList.contains('is-pending')) return;
    if (parseInt(input.value, 10) === parseInt(input.defaultValue, 10)) {
      clearTimeout(timers[li.getAttribute('data-key')]);
      return;
    }
    schedule(li, false);
  });

  document.addEventListener('keydown', function (event) {
    var target = event.target;
    if (event.key === 'Enter' && target.matches && target.matches('.fd-qty__input')) {
      // Apply immediately instead of letting the form submit.
      event.preventDefault();
      var li = target.closest('[data-fd-cart-line]');
      if (!li) return;
      target.dispatchEvent(new Event('change', { bubbles: true }));
      flush(li);
    } else if (event.key === ' ' && target.matches && target.matches('a[role="button"][data-fd-open]')) {
      event.preventDefault();
      target.click();
    }
  });

  document.addEventListener('click', function (event) {
    var remove = event.target.closest('[data-fd-cart-remove]');
    if (!remove) return;
    event.preventDefault();
    var li = remove.closest('[data-fd-cart-line]');
    if (!li || li.classList.contains('is-pending')) return;
    var input = li.querySelector('.fd-qty__input');
    if (!input) return;
    input.value = '0';
    schedule(li, true);
  });

  /* ---------- Drawer: remember the opener, however it was opened ---------- */
  function wireDrawer(dialog) {
    if (!dialog || dialog._fdCartWired || typeof dialog.showModal !== 'function') return;
    dialog._fdCartWired = true;
    var nativeShow = dialog.showModal.bind(dialog);
    var opener = null;

    // Patched so both [data-fd-open] and FD.openCart() record the opener
    // before the browser moves focus into the dialog.
    dialog.showModal = function () {
      if (!dialog.open) {
        opener = document.activeElement;
        nativeShow();
      }
    };

    dialog.addEventListener('close', function () {
      // The header badge is re-rendered, so the original opener may be detached.
      var target = opener && opener.isConnected ? opener : document.querySelector(OPENER_SELECTOR);
      opener = null;
      if (target && typeof target.focus === 'function') target.focus();
      upgradeOpeners();
    });
  }

  // The header link is a plain anchor without JS; with a drawer it acts as a button.
  function upgradeOpeners() {
    var drawer = document.querySelector(DRAWER_SELECTOR);
    if (!drawer) return;
    Array.prototype.forEach.call(document.querySelectorAll(OPENER_SELECTOR), function (el) {
      if (el.tagName !== 'A') return;
      el.setAttribute('role', 'button');
      el.setAttribute('aria-haspopup', 'dialog');
      el.setAttribute('aria-expanded', drawer.open ? 'true' : 'false');
    });
  }

  wireDrawer(document.querySelector(DRAWER_SELECTOR));
  upgradeOpeners();
  FD.on('fd:cart:updated', upgradeOpeners);
})();
